import React, { useState } from 'react';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { Box, Icon, Link, Spacer } from '@chakra-ui/react';
import { useAtom } from 'jotai';
import NextLink from 'next/link';
import { useRouter } from 'next/router';

import { favoriteMenuItemKeysAtom } from '@/store/favorite-menu-items/favorite-menu-items.store';
import { colors } from '@/styles/ui.theme';

interface Props {
  menuItemKey: string;
  href: string;
  primaryText: string;
  leftIcon: React.ReactNode;
}

type FavoriteIconProps = {
  menuItemKey: string;
};

const FavoriteIcon = ({ menuItemKey }: FavoriteIconProps): React.ReactElement => {
  const [favoriteMenuItemKeys, setFavoriteMenuItems] = useAtom(favoriteMenuItemKeysAtom);
  const isFavorite = favoriteMenuItemKeys.includes(menuItemKey);
  const toggleIcon = isFavorite ? AiFillStar : AiOutlineStar;

  const newFavorites = isFavorite
    ? favoriteMenuItemKeys.filter((item) => item !== menuItemKey)
    : [...favoriteMenuItemKeys, menuItemKey];

  return <Icon as={toggleIcon} onClick={() => setFavoriteMenuItems(newFavorites)} />;
};

const normalizePath = (path: string): string => {
  const clean = path.split('?')[0].split('#')[0];
  return clean === '/' ? '/' : clean.replace(/\/$/, '');
};


const SideListItem = ({ menuItemKey, href, primaryText, leftIcon }: Props): React.ReactElement => {
  const router = useRouter();
  const [isMouseOver, setMouseOver] = useState(false);
  const normalizedAsPath = normalizePath(router.asPath);
  const normalizedHref = normalizePath(href);
  const isSelected = normalizedAsPath === normalizedHref;
  const isActive = isMouseOver || isSelected;

  return (
    <NextLink href={href} passHref>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore - Complex union type with Chakra UI components */}
      <Link
        onMouseOver={() => setMouseOver(true)}
        onFocus={() => setMouseOver(true)}
        onMouseLeave={() => setMouseOver(false)}
        onBlur={() => setMouseOver(false)}
        maxH='42px'
        display='flex'
        alignItems='center'
        p='21px 0px 21px 0px'
        textDecoration='none'
        backgroundPosition='center'
        transition='background 0.8s'
        _hover={{
          background: `${colors.descomplica.gray['secondary-gray-10']} radial-gradient(circle, transparent 1%, ${colors.descomplica.gray['secondary-gray-10']} 1%) center/15000%`,
        }}
        _active={{
          backgroundColor: colors.descomplica.gray['secondary-gray-10'],
          backgroundSize: '100%',
          transition: 'background 0s',
        }}
      >
        {isActive && (
          <Box
            position='relative'
            top='0'
            left='0'
            width='4px'
            height='42px'
            backgroundColor={colors.descomplica.green.dark}
          />
        )}
        <Box
          display='flex'
          mr='18px'
          ml={isActive ? '32px' : '36px'}
          color={
            isActive
              ? colors.descomplica.green.primary
              : colors.descomplica.gray['secondary-gray-60']
          }
        >
          {leftIcon}
        </Box>
        <Box
          color={
            isActive
              ? colors.descomplica.green.primary
              : colors.descomplica.gray['secondary-gray-60']
          }
          noOfLines={1}
        >
          {primaryText}
        </Box>

        <Spacer />

        <Box
          display='flex'
          mr='18px'
          ml={isActive ? '32px' : '36px'}
          color={
            isActive
              ? colors.descomplica.green.primary
              : colors.descomplica.gray['secondary-gray-60']
          }
        >
          <FavoriteIcon menuItemKey={menuItemKey} />
        </Box>
      </Link>
    </NextLink>
  );
};

export default SideListItem;
